// Item-rendering surface for `RequestBoxesView`. Extracted as part of
// the #1178 pre-decomposition (§7 step 5) so the parent file has
// headroom for the regroup edit-mode UI without breaching the 600-line
// hard cap. No behavioral change; same desktop table + mobile cards +
// divider rules from #704, same visibility gate.

import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { RequestBoxItemView } from '../../types.js';
import { BoxItemThumb } from './BoxItemThumb.js';

export function BoxItemsTableDesktop({
  requestId,
  boxId,
  items,
}: {
  requestId: string;
  boxId: string;
  items: ReadonlyArray<RequestBoxItemView>;
}) {
  const { t } = useTranslation(['requests']);
  // WHY (#704): bucket items by `groupOrdinal` (preserving stored
  //   `(sortOrder, id)` order within each bucket) so we can insert a
  //   visual divider between groups. The numbering `i + 1` is the
  //   render-time row number across the bucketed stream so it still
  //   matches what the manager actually reads; the divider does not
  //   interrupt it.
  const ordered = bucketItemsByGroup(items);
  let renderedIdx = 0;
  let prevOrdinal: number | null = null;
  return (
    <div style={itemsTableStyle}>
      <div style={itemHeaderCellStyle}>№</div>
      <div style={itemHeaderCellStyle}>{t('requests:table.photos')}</div>
      <div style={itemHeaderCellStyle}>{t('requests:table.title')}</div>
      <div style={itemHeaderCellStyle}>{t('requests:table.description')}</div>
      <div style={itemHeaderCellStyle}>{t('requests:table.brand')}</div>
      <div style={itemHeaderCellStyle}>{t('requests:table.material')}</div>
      <div style={{ ...itemHeaderCellStyle, justifyContent: 'flex-end' }}>
        {/* WHY (#1056): nested rows carry the product count per box,
            not places — relabel the column to productQuantity. */}
        {t('requests:table.productQuantity')}
      </div>
      {ordered.map((it) => {
        const dividerBefore = prevOrdinal !== null && it.groupOrdinal !== prevOrdinal;
        prevOrdinal = it.groupOrdinal;
        const i = renderedIdx;
        renderedIdx += 1;
        return (
          <Fragment key={it.id}>
            {dividerBefore && <BoxGroupDividerDesktop />}
            <BoxItemRowDesktop requestId={requestId} boxId={boxId} item={it} index={i} />
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * Sort wire items into the manager's read order. The wire emits items
 * in `(sortOrder, id)` regardless of `groupOrdinal`, so a logist
 * reassigning a middle item produces a stream like `[g0, g1, g0]`.
 * Bucket by ordinal first to keep same-group items adjacent and emit
 * a single divider between groups; preserve relative wire order
 * within each bucket so item numbering stays stable.
 */
function bucketItemsByGroup(
  items: ReadonlyArray<RequestBoxItemView>,
): ReadonlyArray<RequestBoxItemView> {
  return items
    .map((it, idx) => ({ it, idx }))
    .sort((a, b) => a.it.groupOrdinal - b.it.groupOrdinal || a.idx - b.idx)
    .map((e) => e.it);
}

/**
 * Visual divider between `groupOrdinal` buckets (#704). Spans every
 * column in the parent CSS grid so the line breaks the row stream
 * cleanly. No label by default (out-of-scope per #704); a future
 * iteration may add a group label here.
 */
function BoxGroupDividerDesktop() {
  return (
    <div
      data-testid="box-group-divider"
      style={{
        gridColumn: '1 / -1',
        height: 0,
        borderTop: '2px dashed var(--border-1)',
        margin: '4px 0',
      }}
    />
  );
}

function BoxItemRowDesktop({
  requestId,
  boxId,
  item,
  index,
}: {
  requestId: string;
  boxId: string;
  item: RequestBoxItemView;
  index: number;
}) {
  const objectKey = item.photoKeys[0] ?? null;
  return (
    <>
      <div style={{ ...itemCellStyle, color: 'var(--fg-3)' }}>{index + 1}</div>
      <div style={itemCellStyle}>
        <BoxItemThumb
          requestId={requestId}
          boxId={boxId}
          boxItemId={item.id}
          objectKey={objectKey}
        />
      </div>
      <div style={{ ...itemCellStyle, fontWeight: 500, wordBreak: 'break-word' }}>
        {item.name || '—'}
      </div>
      <div
        style={{
          ...itemCellStyle,
          color: 'var(--fg-2)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {item.description || '—'}
      </div>
      <div style={{ ...itemCellStyle, color: 'var(--fg-2)', wordBreak: 'break-word' }}>
        {item.brand || '—'}
      </div>
      <div
        style={{
          ...itemCellStyle,
          color: 'var(--fg-2)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {item.material || '—'}
      </div>
      <div
        style={{
          ...itemCellStyle,
          justifyContent: 'flex-end',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {item.quantity ?? '—'}
      </div>
    </>
  );
}

function BoxItemCardMobile({
  requestId,
  boxId,
  item,
  index,
}: {
  requestId: string;
  boxId: string;
  item: RequestBoxItemView;
  index: number;
}) {
  const { t } = useTranslation(['requests']);
  const objectKey = item.photoKeys[0] ?? null;
  return (
    <div style={mobileItemCardStyle}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ color: 'var(--fg-3)', fontSize: 12, minWidth: 18 }}>{index + 1}</div>
        <BoxItemThumb
          requestId={requestId}
          boxId={boxId}
          boxItemId={item.id}
          objectKey={objectKey}
        />
        <div style={{ display: 'grid', gap: 2, fontSize: 12, lineHeight: 1.35, minWidth: 0 }}>
          <div style={{ color: 'var(--fg-1)', fontWeight: 600, wordBreak: 'break-word' }}>
            {item.name || '—'}
          </div>
          <div style={{ color: 'var(--fg-3)' }}>
            <span style={mobilePropLabelStyle}>{t('requests:table.brand')}</span>{' '}
            {item.brand || '—'}
            {item.material && item.material.length > 0 && (
              <>
                {' · '}
                <span style={mobilePropLabelStyle}>{t('requests:table.material')}</span>{' '}
                {item.material}
              </>
            )}
            {item.quantity !== null && (
              <>
                {' · '}
                <span style={mobilePropLabelStyle}>{t('requests:table.productQuantity')}</span>{' '}
                {item.quantity}
              </>
            )}
          </div>
          {item.description.trim().length > 0 && (
            <div
              style={{
                color: 'var(--fg-2)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {item.description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile row stream — same grouping rule as the desktop table (#704)
 * but the divider is rendered between `<li>` siblings. The numbering
 * still increments across groups so the read order matches what the
 * exporter / logist sees.
 */
export function renderMobileItems(
  requestId: string,
  boxId: string,
  items: ReadonlyArray<RequestBoxItemView>,
): ReactNode[] {
  const out: ReactNode[] = [];
  let prevOrdinal: number | null = null;
  let renderedIdx = 0;
  for (const it of bucketItemsByGroup(items)) {
    const dividerBefore = prevOrdinal !== null && it.groupOrdinal !== prevOrdinal;
    prevOrdinal = it.groupOrdinal;
    if (dividerBefore) {
      out.push(
        <li
          key={`divider-before-${it.id}`}
          data-testid="box-group-divider"
          style={{
            height: 0,
            borderTop: '2px dashed var(--border-1)',
            margin: '4px 0',
            listStyle: 'none',
          }}
        />,
      );
    }
    const i = renderedIdx;
    renderedIdx += 1;
    out.push(
      <li key={it.id}>
        <BoxItemCardMobile requestId={requestId} boxId={boxId} item={it} index={i} />
      </li>,
    );
  }
  return out;
}

// WHY: hide anonymous nested rows (no name, no brand, no photo, no
//   material) so a stray empty wire row doesn't render as a `—`-only
//   line in the read-only drawer. Material was added to this gate in
//   #1055 — the draft editor allows a nested item with material only,
//   so the read view must surface it for inspection. The XLSX export
//   deliberately does NOT mirror this hide rule — it writes every
//   nested row including blanks.
export function isVisibleBoxItem(item: RequestBoxItemView): boolean {
  return (
    item.name.trim().length > 0 ||
    item.brand.trim().length > 0 ||
    item.photoKeys.length > 0 ||
    (item.material ?? '').trim().length > 0
  );
}

const itemsTableStyle: CSSProperties = {
  display: 'grid',
  // WHY (#1055): added a `material` column between brand and quantity.
  //   The grid now carries 7 columns to keep the read shape aligned with
  //   what the draft editor surfaces.
  gridTemplateColumns:
    '40px 70px minmax(140px, 1.2fr) minmax(180px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr) 80px',
  borderTop: '1px solid var(--border-2)',
};

const itemHeaderCellStyle: CSSProperties = {
  padding: '0 10px',
  height: 28,
  display: 'flex',
  alignItems: 'center',
  fontSize: 10,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  fontWeight: 600,
  color: 'var(--fg-3)',
  borderBottom: '1px solid var(--border-1)',
};

const itemCellStyle: CSSProperties = {
  padding: '8px 10px',
  display: 'flex',
  alignItems: 'flex-start',
  borderBottom: '1px solid var(--border-2)',
  fontSize: 13,
};

const mobileItemCardStyle: CSSProperties = {
  border: '1px solid var(--border-1)',
  borderRadius: 4,
  padding: 8,
  background: 'var(--bg-1)',
};

const mobilePropLabelStyle: CSSProperties = {
  fontSize: 10,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'var(--fg-3)',
};
